import { MutableRefObject } from 'react';
import reduce from 'lodash/reduce';
import isEqual from 'lodash/isEqual';
import { TAdditionalService } from '../components/postjob/rolecard/agentview/AgentDelegateCard';
import { TJobItem, TMatchingMode, TServicesStructure } from '../types';
import { matchingModeCodes, matchingModeLabels } from '../consts';

export const scrollTo = (element: Element) => {
    element.scrollIntoView({ behavior: 'smooth' });
};

export const addTextareaInvalidClass = (ref: MutableRefObject<any>) => {
    ref.current.classList.add('textarea-invalid');
    ref.current.placeholder = 'Required!';
};

export const removeTextareaInvalidClass = (ref: MutableRefObject<any>) => {
    ref.current.classList.remove('textarea-invalid');
};

export const getDisplayName = (WrappedComponent: {
    displayName: any;
    name: any;
}) => WrappedComponent.displayName || WrappedComponent.name || 'Component';

// ObjToKeyValue<T> turns an object type into a union of key/value tuples:
// ObjToKeyValue<{a: string, b: number}> becomes ["a", string] | ["b", number]
type ObjToKeyValue<T> = { [K in keyof T]: [K, T[K]] }[keyof T];

// KeyValueToObj<T> turns a union of key/value tuples into an object type:
// KeyValueToObj<["a", string] | ["b", number]> becomes {a: string, b: number}
type KeyValueToObj<KV extends [keyof any, any]> = {
    [K in KV[0]]: KV extends [K, infer V] ? V : never;
};

// ReverseTuple<KV> swaps the keys and values in a union of key/value tuples:
// ReverseTuple<[1, 2] | [3, 4]> becomes [2, 1] | [4, 3]
type ReverseTuple<KV extends [any, any]> = KV extends [any, any]
    ? [KV[1], KV[0]]
    : never;

// ReverseObj<T> takes an object type whose properties are valid keys
// and returns a new object type where the keys and values are swapped:
// ReverseObj<{a: "b", c: "d"}> becomes {b: "a", d: "c"}
type ReverseObj<T extends Record<keyof T, keyof any>> = KeyValueToObj<
    ReverseTuple<ObjToKeyValue<T>>
>;

export const doubleDictionary = <
    S extends keyof any, // infer literals for property values if possible
    T extends Record<keyof T, S>,
>(
    t: T,
) => {
    const ret = { ...t } as T & ReverseObj<T>; // return type
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const k in t) {
        ret[t[k]] = k as any; // need assertion here, compiler can't verify k
    }
    return ret;
};

interface PayloadData {
    [key: string]: string | number | boolean | any[];
}

export const camelToSnake = (original: PayloadData): PayloadData => {
    const newObject: PayloadData = {};

    Object.keys(original).forEach(originalKey => {
        const newKey = originalKey.replace(/([A-Z])/g, '_$1').toLowerCase();

        newObject[newKey] = original[originalKey];
    });

    return newObject;
};

export const isImage = (src: string) => src.match(/\.(jpeg|jpg|gif|png)((\?)?|$)/) !== null;

export const reduceObject = (sourceObj: TAdditionalService, keys: string[]) =>
    /**
     * Reduces an object to include only specified keys.
     * E.g.: reduceObject({a: 1, b: 2, c: 3, d: 4}, ['b', 'c']) returns {b: 2, c: 3}
     * @param sourceObj - the source object to reduce.
     * @param keys - the keys to include in the reduced object.
     */
    // @ts-ignore
    keys.reduce((obj: TAdditionalService, key) => ({ ...obj, [key]: sourceObj[key] }), {});

export const reduceDuplicatedKeyValPairs = (firstObj: {[key: string]: any}, secondObj: {[key: string]: any}) =>
    /**
     * Reduces an object to exclude duplicated key-value pairs.
     * E.g.: reduceDuplicatedKeyValPairs(
     *     { a: 1, b: { c: 3 }, d: [4, 5] },
     *     { a: 1, b: { c: 30 }, d: [4, 5], e: 9 }
     * ) returns { b: { c: 3 } }
     * @param firstObj - the source object to reduce.
     * @param secondObj - the other object to compare key-value pairs.
     */
    reduce(firstObj, (acc: {[key: string]: any}, value, key) => {
        if (!isEqual(value, secondObj[key])) {
            acc[key] = value;
        }
        return acc;
    }, {});

export const getRelevantJobId = (job: TJobItem) =>
    /**
     * Returns the id of a job corresponding to the booking row
     * @param job - the Job object processed by flattenBookingsToJobs function
     */
    job.bookings.map(booking => booking.id).includes(job.booking.id)
        ? job.id
        : job.additional_services.find(child =>
            child.bookings.map(childBooking => childBooking.id).includes(job.booking.id)
        ).id;

export const findKeyByValue = (object: {[key: string]: string}, value: string) =>
    /**
     * Returns the key of an object by its value
     * @param object - the object to search
     * @param value - the value to find
     */
    Object.keys(object).find(key => object[key] === value);

// @ts-ignore
export const getMatchingModeLabel = (m: TMatchingMode) => matchingModeLabels[findKeyByValue(matchingModeCodes, m)];

export const getAllSkills = (servicesStructure: TServicesStructure) => {
    /**
     * Returns all skills from the services structure
     * @param servicesStructure - the services structure
     */
    const skills: string[] = Object.entries(servicesStructure)
        .filter(([service]) => service)
        .map(([, serviceStructure]) => serviceStructure)
        .map(serviceStructure => serviceStructure[serviceStructure.role_filter_name])
        .flatMap(Object.values)
        .flatMap(Object.keys)
        .filter(skill => !['label', 'icon_name'].includes(skill));

    // return unique skills
    return [...new Set(skills)];
};
